import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import {
  MainTableColumnId,
  MainTableColumn,
  MainTableSorting,
} from '@prlw/core/monitor/types/main-table';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { ResponsiveService } from '@prlw/libs/responsive/responsive.service';

export type Indicator = 'offer' | 'deal' | 'demand';

@Component({
  selector: 'prlw-main-table-header-ui',
  templateUrl: './main-table-header.component.html',
  styleUrls: ['./main-table-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MainTableHeaderComponent {
  @Input() public columns: MainTableColumn<MainTableColumnId>[] | null = null;
  @Input() public sorting: MainTableSorting | null = null;
  @Input() public gridSize: string | null = null;
  @Input()
  public isModifiersOverlayOpen: boolean | null = null;
  @Input() public isModifierChanged: boolean | null = null;
  @Input() public preloaderActive: boolean | null = null;
  @Input() public reqInstrumentsResult: unknown | null = null;

  @Output()
  public readonly sortColumn = new EventEmitter<MainTableColumnId>();
  @Output()
  public readonly openModifiersOverlay = new EventEmitter<void>();
  @Output()
  public readonly eventUpdateColumns = new EventEmitter<
    MainTableColumn<MainTableColumnId>[]
  >();

  constructor(private readonly responsiveService: ResponsiveService) {}

  xsmall$ = this.responsiveService.xsmall$;

  public drop(event: CdkDragDrop<MainTableColumn<MainTableColumnId>[]>): void {
    if (
      this.columns &&
      event.previousIndex !== 0 &&
      !this.columns[event.currentIndex].isStatic
    ) {
      moveItemInArray(this.columns, event.previousIndex, event.currentIndex);
      this.eventUpdateColumns.emit(this.columns);
    }
  }

  public get isDesSort(): boolean {
    return this.sorting?.direction === 'DES';
  }

  public isActionActive(columnId: MainTableColumnId): boolean {
    return (
      columnId === MainTableColumnId.LOCAL_PRICE &&
      Boolean(this.isModifiersOverlayOpen)
    );
  }

  public isActionChanged(columnId: MainTableColumnId): boolean {
    return (
      columnId === MainTableColumnId.LOCAL_PRICE &&
      (Boolean(this.isModifierChanged) ||
        this.hasSorting(MainTableColumnId.LOCAL_PRICE))
    );
  }

  public hasSorting(columnId: MainTableColumnId): boolean {
    return columnId === this.sorting?.columnId;
  }

  public isBasis(columnId: MainTableColumnId): boolean {
    return columnId === MainTableColumnId.BASIS;
  }

  public getIndicator(columnId: MainTableColumnId): Indicator | null {
    const indicators: { [key: number]: Indicator | undefined } = {
      [MainTableColumnId.OFFER]: 'offer',
      [MainTableColumnId.DEAL]: 'deal',
      [MainTableColumnId.DEMAND]: 'demand',
    };

    return indicators[columnId] ?? null;
  }

  public hasAction(columnId: MainTableColumnId): boolean {
    return columnId === MainTableColumnId.LOCAL_PRICE;
  }

  public action(columnId: MainTableColumnId): void {
    if (this.hasAction(columnId)) {
      this.openModifiersOverlay.emit();
    } else {
      this.sortColumn.emit(columnId);
    }
  }
}
